import { useState } from 'react';
import type { ReactNode, ButtonHTMLAttributes, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

/* ------------------------------------------------------------------ Card */

export function Card({
  title,
  subtitle,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('rounded-xl border border-ink-200 bg-white shadow-sm', className)}>
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 border-b border-ink-100 px-5 py-4">
          <div>
            {title && <h2 className="text-sm font-semibold text-ink-900">{title}</h2>}
            {subtitle && <p className="mt-1 text-xs leading-relaxed text-ink-500">{subtitle}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

/* ---------------------------------------------------------------- Button */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  variant = 'secondary',
  className,
  loading,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; loading?: boolean }) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50';
  const variants: Record<ButtonVariant, string> = {
    primary: 'bg-ink-900 text-white hover:bg-ink-800',
    secondary: 'border border-ink-200 bg-white text-ink-700 hover:bg-ink-50',
    ghost: 'text-ink-600 hover:bg-ink-100',
    danger: 'bg-red-600 text-white hover:bg-red-700',
  };
  return (
    <button className={cx(base, variants[variant], className)} disabled={loading || rest.disabled} {...rest}>
      {loading && <Spinner small />}
      {children}
    </button>
  );
}

/* ----------------------------------------------------------------- Badge */

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'info';
  className?: string;
}) {
  const tones = {
    neutral: 'bg-ink-100 text-ink-700 border-ink-200',
    good: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warn: 'bg-amber-50 text-amber-700 border-amber-200',
    bad: 'bg-red-50 text-red-700 border-red-200',
    info: 'bg-sky-50 text-sky-700 border-sky-200',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
        tones[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ Stat */

export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'good' | 'warn' | 'bad';
}) {
  const toneClass =
    tone === 'good' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : tone === 'bad' ? 'text-red-700' : 'text-ink-900';
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</div>
      <div className={cx('mt-2 text-2xl font-semibold tabular-nums', toneClass)}>{value}</div>
      {hint && <div className="mt-1 text-xs leading-relaxed text-ink-500">{hint}</div>}
    </div>
  );
}

/**
 * The dual metric tile is used everywhere engagement is shown: the filtered
 * number is the headline, the unfiltered number and the excluded share sit
 * underneath so nothing is hidden.
 */
export function DualStat({
  label,
  filtered,
  raw,
  excludedPct,
  suffix = '',
}: {
  label: string;
  filtered: number;
  raw: number;
  excludedPct: number;
  suffix?: string;
}) {
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-ink-900">
          {filtered.toLocaleString()}
          {suffix}
        </span>
        <span className="text-xs text-ink-400">filtered</span>
      </div>
      <div className="mt-1.5 flex items-center justify-between text-xs text-ink-500">
        <span className="tabular-nums">
          {raw.toLocaleString()}
          {suffix} unfiltered
        </span>
        <Badge tone={excludedPct > 40 ? 'warn' : 'neutral'}>{excludedPct.toFixed(1)}% removed</Badge>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Table */

export function Table({ head, children }: { head: ReactNode[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-full border-collapse text-sm">
        <thead>
          <tr className="table-head">
            {head.map((cell, i) => (
              <th key={i} className="whitespace-nowrap px-3 py-2.5 first:rounded-l-md last:rounded-r-md">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">{children}</tbody>
      </table>
    </div>
  );
}

export function Td({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cx('px-3 py-2.5 align-top text-ink-700', className)}>{children}</td>;
}

/* ----------------------------------------------------------------- Forms */

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}

const inputClass =
  'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ink-400 focus:outline-none focus:ring-2 focus:ring-ink-200';

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(inputClass, props.className)} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cx(inputClass, 'font-mono text-xs', props.className)} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(inputClass, props.className)} />;
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2.5 text-sm text-ink-700"
    >
      <span
        className={cx(
          'relative h-5 w-9 rounded-full transition',
          checked ? 'bg-ink-900' : 'bg-ink-300'
        )}
      >
        <span
          className={cx(
            'absolute top-0.5 h-4 w-4 rounded-full bg-white transition',
            checked ? 'left-4.5 translate-x-[14px]' : 'left-0.5'
          )}
        />
      </span>
      {label}
    </button>
  );
}

/* ------------------------------------------------------------------ Copy */

/**
 * Copies a value to the clipboard.
 *
 * DNS records are long, exact strings that are miserable to retype and easy
 * to corrupt by hand, so everything copyable gets one of these. Falls back
 * to a hidden textarea where the Clipboard API is unavailable, and reports
 * failure rather than silently claiming success.
 */
export function CopyButton({
  value,
  label = 'Copy',
  title,
}: {
  value: string;
  label?: string;
  title?: string;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function copy() {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const area = document.createElement('textarea');
        area.value = value;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(area);
        if (!ok) throw new Error('copy rejected');
      }
      setState('copied');
    } catch {
      setState('failed');
    }
    window.setTimeout(() => setState('idle'), 1800);
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={title ?? 'Copy ' + value}
      className={cx(
        'shrink-0 rounded border px-1.5 py-0.5 font-sans text-[11px] transition',
        state === 'copied'
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : state === 'failed'
            ? 'border-red-200 bg-red-50 text-red-700'
            : 'border-ink-200 bg-white text-ink-500 hover:bg-ink-50 hover:text-ink-900'
      )}
    >
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Failed' : label}
    </button>
  );
}

/** A monospace value with a copy button beside it. */
export function CopyableValue({ value, mono = true }: { value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-1.5">
      <code
        className={cx(
          'min-w-0 flex-1 break-all rounded bg-ink-100 px-2 py-1 text-[11px] text-ink-800',
          mono ? 'font-mono' : 'font-sans'
        )}
      >
        {value}
      </code>
      <CopyButton value={value} />
    </div>
  );
}

/* ----------------------------------------------------------------- State */

export function Spinner({ small }: { small?: boolean }) {
  return (
    <span
      className={cx(
        'inline-block animate-spin rounded-full border-2 border-current border-r-transparent',
        small ? 'h-3.5 w-3.5' : 'h-5 w-5'
      )}
    />
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 p-8 text-sm text-ink-500">
      <Spinner /> {label}...
    </div>
  );
}

export function EmptyState({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-ink-200 px-6 py-10 text-center">
      <p className="text-sm font-medium text-ink-700">{title}</p>
      {body && <p className="mx-auto mt-1.5 max-w-md text-xs leading-relaxed text-ink-500">{body}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'bad' | 'good';
  title?: string;
  children: ReactNode;
}) {
  const tones = {
    info: 'border-sky-200 bg-sky-50 text-sky-900',
    warn: 'border-amber-200 bg-amber-50 text-amber-900',
    bad: 'border-red-200 bg-red-50 text-red-900',
    good: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  };
  return (
    <div className={cx('rounded-lg border px-4 py-3 text-sm leading-relaxed', tones[tone])}>
      {title && <p className="mb-1 font-semibold">{title}</p>}
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-xl font-semibold text-ink-900">{title}</h1>
        {description && <p className="mt-1 max-w-3xl text-sm leading-relaxed text-ink-500">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ id: string; label: string }>;
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="mb-5 flex gap-1 border-b border-ink-200">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={cx(
            '-mb-px border-b-2 px-3.5 py-2 text-sm font-medium transition',
            active === tab.id
              ? 'border-ink-900 text-ink-900'
              : 'border-transparent text-ink-500 hover:text-ink-700'
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
